/**
 * Coverage tab — pending / validated / rejected counts + taxonomy blockers,
 * with click-through navigation (Level 1) and per-gap drilldown expanders
 * (Level 2). All numbers here act as links into the Mappings or Form Bindings
 * queue with filters pre-applied.
 */
import React, { useEffect, useState } from 'react';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import {
  getCoverage,
  type TaxonomyCoverageReport,
  type CoverageGapRow,
} from './caseSubtypeTaxonomyApi';
import type { TaxonomyNavTarget } from './CaseSubtypeTaxonomyPanel';

interface Props {
  stateCode: string;
  practiceArea: string;
  onNavigate: (target: TaxonomyNavTarget) => void;
}

const METRIC_CARD_BASE = 'rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30 p-4';

type GapKey =
  | 'validated_mappings_without_form_bindings'
  | 'validated_mappings_without_workflow'
  | 'validated_mappings_without_template'
  | 'validated_mappings_without_playbook_scheme';

const BLOCKER_TO_GAP: Record<string, GapKey | undefined> = {
  validated_mapping_without_form_bindings: 'validated_mappings_without_form_bindings',
  validated_mapping_without_workflow: 'validated_mappings_without_workflow',
  validated_mapping_without_template: 'validated_mappings_without_template',
  validated_mapping_without_playbook_scheme: 'validated_mappings_without_playbook_scheme',
};

const CoverageOverview: React.FC<Props> = ({ stateCode, practiceArea, onNavigate }) => {
  const [report, setReport] = useState<TaxonomyCoverageReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedGap, setExpandedGap] = useState<GapKey | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getCoverage({
      state_code: stateCode || undefined,
      practice_area: practiceArea || undefined,
      include_gap_rows: true,
    })
      .then((r) => { if (!cancelled) setReport(r.coverage); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load coverage'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [stateCode, practiceArea]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-violet-500 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 rounded-lg p-3 text-sm">
        {error}
      </div>
    );
  }

  if (!report) return null;

  const toggleGap = (key: GapKey) => setExpandedGap((cur) => (cur === key ? null : key));

  return (
    <div className="space-y-5" style={{ animation: 'fadeSlideIn 300ms ease-out forwards' }}>
      {/* Mapping counters */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
          Mappings
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <ClickableMetric
            icon={ClockIcon}
            iconClass="text-amber-700 dark:text-amber-300"
            label="Pending"
            value={report.case_subtype_pending_mapping_count}
            onClick={() => onNavigate({ tab: 'mappings', status: 'pending' })}
          />
          <ClickableMetric
            icon={CheckCircleIcon}
            iconClass="text-emerald-700 dark:text-emerald-300"
            label="Validated"
            value={report.case_subtype_validated_mapping_count}
            onClick={() => onNavigate({ tab: 'mappings', status: 'validated' })}
          />
          <ClickableMetric
            icon={XCircleIcon}
            iconClass="text-rose-700 dark:text-rose-300"
            label="Rejected"
            value={report.case_subtype_rejected_mapping_count}
            onClick={() => onNavigate({ tab: 'mappings', status: 'rejected' })}
          />
        </div>
      </div>

      {/* Form binding counters */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
          Form Bindings
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <ClickableMetric
            icon={ClockIcon}
            iconClass="text-amber-700 dark:text-amber-300"
            label="Pending"
            value={report.form_binding_pending_count}
            onClick={() => onNavigate({ tab: 'form_bindings', status: 'pending' })}
          />
          <ClickableMetric
            icon={CheckCircleIcon}
            iconClass="text-emerald-700 dark:text-emerald-300"
            label="Validated"
            value={report.form_binding_validated_count}
            onClick={() => onNavigate({ tab: 'form_bindings', status: 'validated' })}
          />
          <ClickableMetric
            icon={ExclamationTriangleIcon}
            iconClass="text-rose-700 dark:text-rose-300"
            label="Form-number drift"
            value={report.form_binding_with_drift_count}
            onClick={() => onNavigate({ tab: 'form_bindings', status: 'validated', driftOnly: true })}
          />
        </div>
      </div>

      {/* Validated-mapping downstream gaps */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
          Downstream Gaps (validated mappings missing content)
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <GapCell
            label="No form bindings"
            value={report.validated_mappings_without_form_bindings}
            expanded={expandedGap === 'validated_mappings_without_form_bindings'}
            onToggle={() => toggleGap('validated_mappings_without_form_bindings')}
          />
          <GapCell
            label="No workflow def"
            value={report.validated_mappings_without_workflow}
            expanded={expandedGap === 'validated_mappings_without_workflow'}
            onToggle={() => toggleGap('validated_mappings_without_workflow')}
          />
          <GapCell
            label="No task template"
            value={report.validated_mappings_without_template}
            expanded={expandedGap === 'validated_mappings_without_template'}
            onToggle={() => toggleGap('validated_mappings_without_template')}
          />
          <GapCell
            label="No playbook scheme"
            value={report.validated_mappings_without_playbook_scheme}
            expanded={expandedGap === 'validated_mappings_without_playbook_scheme'}
            onToggle={() => toggleGap('validated_mappings_without_playbook_scheme')}
          />
        </div>
        {expandedGap && report.gap_rows && (
          <GapDrilldown
            gapKey={expandedGap}
            rows={report.gap_rows[expandedGap] || []}
            onNavigate={onNavigate}
          />
        )}
      </div>

      {/* Blocker list */}
      {report.taxonomy_blockers.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
            Blockers
          </h3>
          <ul className="space-y-2">
            {report.taxonomy_blockers.map((b) => {
              const mappedGap = BLOCKER_TO_GAP[b.code];
              const clickable = Boolean(mappedGap) || b.code === 'form_number_drift';
              const handleClick = () => {
                if (mappedGap) {
                  toggleGap(mappedGap);
                } else if (b.code === 'form_number_drift') {
                  onNavigate({ tab: 'form_bindings', status: 'validated', driftOnly: true });
                }
              };
              return (
                <li
                  key={b.code}
                  onClick={clickable ? handleClick : undefined}
                  className={`flex items-start gap-2.5 p-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 ${
                    clickable ? 'cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors' : ''
                  }`}
                >
                  <DocumentTextIcon className="h-4 w-4 mt-0.5 text-amber-700 dark:text-amber-300 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{b.code}</span>
                      <span className="text-xs font-semibold text-amber-700 dark:text-amber-300">
                        {b.count}
                      </span>
                      {clickable && (
                        <span className="text-[10px] uppercase tracking-wider text-amber-700/70 dark:text-amber-300/70">
                          {mappedGap ? 'click to inspect rows' : 'click to review drifted bindings'}
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">{b.detail}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

// ─── Sub-components ───

const ClickableMetric: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  iconClass: string;
  label: string;
  value: number;
  onClick: () => void;
}> = ({ icon: Icon, iconClass, label, value, onClick }) => (
  <button
    onClick={onClick}
    className={`${METRIC_CARD_BASE} text-left hover:border-violet-300 dark:hover:border-violet-700 hover:shadow-sm transition-all cursor-pointer group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500`}
    aria-label={`${label}: ${value}. Click to open filtered review queue.`}
  >
    <div className="flex items-center justify-between">
      <div className={`flex items-center gap-2 ${iconClass}`}>
        <Icon className="h-4 w-4" />
        <span className="text-xs font-medium">{label}</span>
      </div>
      <ArrowRightIcon className="h-3.5 w-3.5 text-gray-300 dark:text-gray-600 group-hover:text-violet-500 dark:group-hover:text-violet-400 transition-colors" />
    </div>
    <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">{value}</div>
  </button>
);

const GapCell: React.FC<{
  label: string;
  value: number;
  expanded: boolean;
  onToggle: () => void;
}> = ({ label, value, expanded, onToggle }) => {
  const tone = value > 0 ? 'text-amber-700 dark:text-amber-300' : 'text-gray-400 dark:text-gray-500';
  const clickable = value > 0;
  return (
    <button
      onClick={clickable ? onToggle : undefined}
      disabled={!clickable}
      className={`${METRIC_CARD_BASE} text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 ${
        clickable
          ? 'hover:border-violet-300 dark:hover:border-violet-700 hover:shadow-sm cursor-pointer'
          : 'cursor-default'
      } ${expanded ? 'ring-2 ring-violet-400' : ''}`}
      aria-expanded={expanded}
    >
      <div className="flex items-center justify-between">
        <div className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</div>
        {clickable && (
          expanded
            ? <ChevronDownIcon className="h-3.5 w-3.5 text-violet-500" />
            : <ChevronRightIcon className="h-3.5 w-3.5 text-gray-400" />
        )}
      </div>
      <div className={`mt-1 text-2xl font-semibold ${tone}`}>{value}</div>
    </button>
  );
};

const GAP_TITLES: Record<GapKey, string> = {
  validated_mappings_without_form_bindings: 'Validated mappings with no form bindings',
  validated_mappings_without_workflow: 'Validated mappings with no workflow definition',
  validated_mappings_without_template: 'Validated mappings with no task template',
  validated_mappings_without_playbook_scheme: 'Validated mappings with no playbook scheme',
};

const GapDrilldown: React.FC<{
  gapKey: GapKey;
  rows: CoverageGapRow[];
  onNavigate: (target: TaxonomyNavTarget) => void;
}> = ({ gapKey, rows, onNavigate }) => {
  if (rows.length === 0) {
    return (
      <div
        className="mt-3 rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50/40 dark:bg-violet-900/10 p-3 text-xs text-gray-500 dark:text-gray-400"
        style={{ animation: 'fadeSlideIn 200ms ease-out forwards' }}
      >
        No rows to drill into for {GAP_TITLES[gapKey]}.
      </div>
    );
  }
  return (
    <div
      className="mt-3 rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50/40 dark:bg-violet-900/10 overflow-hidden"
      style={{ animation: 'fadeSlideIn 200ms ease-out forwards' }}
    >
      <div className="px-3 py-2 border-b border-violet-200 dark:border-violet-800 bg-violet-100/60 dark:bg-violet-900/20">
        <span className="text-xs font-semibold uppercase tracking-wider text-violet-800 dark:text-violet-200">
          {GAP_TITLES[gapKey]} · {rows.length}
        </span>
      </div>
      <div className="max-h-64 overflow-y-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 sticky top-0">
            <tr>
              <th className="px-3 py-1.5 text-left font-semibold">Practice Area</th>
              <th className="px-3 py-1.5 text-left font-semibold">Scope</th>
              <th className="px-3 py-1.5 text-left font-semibold">Local → Canonical</th>
              <th className="px-3 py-1.5 text-right font-semibold">Open</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-violet-200/60 dark:divide-violet-800/60">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-white/70 dark:hover:bg-gray-800/40">
                <td className="px-3 py-1.5 text-gray-700 dark:text-gray-200">{r.practice_area}</td>
                <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400">
                  {r.jurisdiction_code || r.state_code || '—'}
                </td>
                <td className="px-3 py-1.5">
                  <div className="font-medium text-gray-900 dark:text-gray-100">{r.local_label}</div>
                  <div className="font-mono text-[10px] text-gray-400 dark:text-gray-500">
                    {r.local_key} → {r.canonical_key}
                  </div>
                </td>
                <td className="px-3 py-1.5 text-right">
                  <button
                    onClick={() =>
                      onNavigate({
                        tab: gapKey === 'validated_mappings_without_form_bindings' ? 'form_bindings' : 'mappings',
                        status: 'validated',
                        canonicalKey: r.canonical_key,
                        highlightId: gapKey === 'validated_mappings_without_form_bindings' ? undefined : r.id,
                      })
                    }
                    className="inline-flex items-center gap-1 rounded-md bg-violet-600 hover:bg-violet-500 text-white px-2 py-0.5 text-[10px] font-medium"
                  >
                    Open
                    <ArrowRightIcon className="h-3 w-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CoverageOverview;
